// src/pages/Login.tsx
import { useState, useEffect, useRef } from 'react';
import {
  TextField, Button, Typography, Box, InputAdornment, Checkbox, FormControlLabel, Stack,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import MailOutline from '@mui/icons-material/MailOutline';
import LockOutlined from '@mui/icons-material/LockOutlined';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { getToken, setAuthSession } from '../utils/auth';
import logo from '../assets/logo.png';
import Recaptcha, { RECAPTCHA_SITE_KEY, RecaptchaHandle } from '../components/ui/Recaptcha';

const drift = keyframes`0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(-26px,20px) scale(1.1)}`;

const REMEMBER_KEY = 'qs.rememberedUsername';

const readRemembered = (): string => {
  try { return localStorage.getItem(REMEMBER_KEY) || ''; } catch { return ''; }
};

/** Chrome / Edge / Android: hand the credentials to the browser's own (encrypted) password manager. */
const storeCredential = async (id: string, secret: string) => {
  try {
    const Ctor = (window as any).PasswordCredential;
    if (Ctor && navigator.credentials) {
      await navigator.credentials.store(new Ctor({ id, password: secret, name: id }));
    }
  } catch { /* unsupported / blocked: the normal browser prompt still applies */ }
};

/** Ask the password manager for a saved login so the form can be pre-filled. */
const loadCredential = async (): Promise<{ id: string; password: string } | null> => {
  try {
    if (!(window as any).PasswordCredential || !navigator.credentials) return null;
    const cred: any = await (navigator.credentials as any).get({ password: true, mediation: 'optional' });
    if (cred?.id && cred?.password) return { id: String(cred.id), password: String(cred.password) };
  } catch { /* ignore */ }
  return null;
};

const POINTS = [
  'Notes with reminders, tags and attachments',
  'Payments, grocery lists and reports',
  'Chat, tunes and protected videos',
];

const Login = () => {
  // "Remember me on this device" keeps you signed in AND pre-fills your username next time.
  // The password is never stored.
  const remembered = readRemembered();
  const [username, setUsername] = useState(remembered);
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [uErr, setUErr] = useState(false);
  const [pErr, setPErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaRef = useRef<RecaptchaHandle>(null);
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    const token = getToken();
    if (token) {
      navigate('/dashboard');
      return;
    }
    // Not signed in: pre-fill from the saved login (stored by the browser, never by this app).
    let cancelled = false;
    loadCredential().then((cred) => {
      if (cancelled || !cred) return;
      setUsername((u) => u || cred.id);
      setPassword((p) => p || cred.password);
    });
    return () => { cancelled = true; };
  }, [navigate]);

  const handleLogin = async () => {
    setUErr(!username.trim());
    setPErr(!password.trim());
    if (!username.trim() || !password.trim()) return;
    if (RECAPTCHA_SITE_KEY && !captchaToken) {
      Swal.fire({ icon: 'warning', title: 'Captcha required', text: 'Please tick "I\'m not a robot" first.' });
      return;
    }

    setBusy(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/auth/login`,
        { username, password, rememberMe, recaptchaToken: captchaToken || undefined }
      );
      setAuthSession({
        token: res.data.token,
        user: res.data.user || {},
        rememberMe,
      });
      try {
        if (rememberMe) localStorage.setItem(REMEMBER_KEY, username.trim());
        else localStorage.removeItem(REMEMBER_KEY);
      } catch { /* storage blocked: sign-in still works */ }
      if (rememberMe) await storeCredential(username.trim(), password);
      navigate('/dashboard');
    } catch (err: any) {
      const message = err.response?.data?.message || 'Login failed. Please try again.';
      Swal.fire({ icon: 'error', title: 'Login Error', text: message });
      captchaRef.current?.reset(); // a captcha token can only be used once
    } finally {
      setBusy(false);
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': { borderRadius: '16px', bgcolor: '#FAF7FF' },
    '& .MuiOutlinedInput-root fieldset': { borderWidth: 2, borderColor: 'rgba(98,38,167,.22)' },
    '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'rgba(98,38,167,.5)' },
    '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#6226A7' },
    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: '0 0 0 5px rgba(98,38,167,.14)' },
  } as const;

  return (
    <Box
      className="qs-animate-in"
      sx={{
        maxWidth: 1536, mx: 'auto', width: '100%', borderRadius: '36px', overflow: 'hidden',
        display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr' },
        bgcolor: '#fff',
        boxShadow: '0 30px 70px rgba(98,38,167,.25)',
        border: '1px solid rgba(98,38,167,.12)',
      }}
    >
      {/* ===== Brand panel ===== */}
      <Box
        sx={{
          position: 'relative', overflow: 'hidden', color: '#fff', p: { xs: 3, md: 5 },
          minHeight: { xs: 240, md: 560 }, display: 'flex', alignItems: 'center',
          background: 'linear-gradient(135deg, #2E0F5E 0%, #6226A7 40%, #B13FD6 75%, #F6A938 130%)',
        }}
      >
        <Box sx={{ position: 'absolute', width: 340, height: 340, right: -80, top: -110, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,214,122,.65), transparent 65%)', '@media (prefers-reduced-motion: no-preference)': { animation: `${drift} 12s ease-in-out infinite` } }} />
        <Box sx={{ position: 'absolute', width: 380, height: 380, left: -120, bottom: -200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,111,216,.5), transparent 65%)' }} />
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(255,255,255,.24), rgba(255,255,255,0) 40%)' }} />

        <Box sx={{ position: 'relative' }}>
          <Box
            component="img"
            src={logo}
            alt="QuillStack"
            sx={{ width: 56, height: 56, borderRadius: '18px', bgcolor: '#fff', p: 0.75, boxShadow: '0 12px 24px rgba(0,0,0,.28)', mb: 2.5 }}
          />
          <Typography sx={{ fontWeight: 900, fontSize: { xs: 30, md: 42 }, lineHeight: 1.08, letterSpacing: '-0.02em', textShadow: '0 4px 18px rgba(0,0,0,.25)' }}>
            Welcome to QuillStack
          </Typography>
          <Typography sx={{ mt: 1, fontWeight: 700, fontSize: { xs: 16, md: 19 }, opacity: 0.95 }}>
            Your ideas, organised.
          </Typography>
          <Stack spacing={1.25} sx={{ mt: 3, display: { xs: 'none', sm: 'flex' } }}>
            {POINTS.map((t) => (
              <Stack key={t} direction="row" spacing={1.25} alignItems="center">
                <CheckCircleRounded sx={{ color: '#FFE29A' }} />
                <Typography sx={{ fontWeight: 600, opacity: 0.97 }}>{t}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* ===== Form ===== */}
      <Box sx={{ p: { xs: 3, md: 5 }, display: 'flex', alignItems: 'center' }}>
        <Box component="form" onSubmit={(e) => { e.preventDefault(); void handleLogin(); }} sx={{ width: '100%' }}>
          <Typography sx={{ fontWeight: 900, fontSize: { xs: 28, md: 34 }, letterSpacing: '-0.02em', color: '#1f1633' }}>
            Sign in
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>Continue to your workspace</Typography>

          <Stack spacing={2.25}>
            <TextField
              label="Username"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (e.target.value.trim()) setUErr(false);
              }}
              error={uErr}
              helperText={uErr ? 'Username is required' : ''}
              fullWidth
              name="username"
              id="qs-login-username"
              autoComplete="username"
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutline sx={{ color: '#6226A7' }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (e.target.value.trim()) setPErr(false);
              }}
              error={pErr}
              helperText={pErr ? 'Password is required' : ''}
              fullWidth
              name="password"
              id="qs-login-password"
              autoFocus={!!remembered}
              autoComplete="current-password"
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined sx={{ color: '#6226A7' }} />
                  </InputAdornment>
                ),
              }}
            />

            <FormControlLabel
              control={<Checkbox checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />}
              label="Remember me on this device"
            />

            <Recaptcha ref={captchaRef} onChange={setCaptchaToken} />

            <Button
              type="submit"
              size="large"
              variant="contained"
              disabled={busy}
              sx={{ py: 1.4, borderRadius: '999px', fontSize: 16, fontWeight: 900 }}
            >
              {busy ? 'Signing in…' : 'Login'}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
